// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/campaign.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import { createElement } from "react";
import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { StageChip } from "../../components/composition/StageChip";
import { campaignsApi } from "../../api/campaigns";
import type { Campaign } from "../../api/types";
// override-block:imports BEGIN
import { ProgressBar } from "../../components/composition/ProgressBar";
import { ACISurface } from "../../components/aci/ACISurface";
import type { CampaignStatus } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1325 — Tag taxonomy phase 3: Tags tab via EntityTagsSection.
import { EntityTagsSection } from "../../components/composition/EntityTagsSection";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Draft",
  active: "Active",
  // CHG-CAMP-005 (R13) — removed invalid "paused" (not a campaign status).
  completed: "Completed",
  expired: "Expired",
  archived: "Archived",
};
// override-block:helpers END

export const campaignDetailConfig: DetailPageConfig<Campaign> = {
  idParam: "campaignId",
  queryKey: (id: string) => ["campaigns", "detail", id] as const,
  queryFn: (id: string) => campaignsApi.getCampaign(id),
  backTo: "/campaigns",
  // override-block:title BEGIN
  title: (r) => r.name,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => r.slug ?? null,
  // override-block:subtitle END
  stageChip: (r) =>
    createElement(StageChip, {
      stage_id: r.stage,
      entity: "campaign",
      size: "lg",
    }),
  statusCards: (r, _fmt) => [
    {
      id: "raised",
      label: "Raised",
      value: r.raised_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-giving",
    },
    {
      id: "goal",
      label: "Goal",
      value: r.goal_amount_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-home",
    },
    // override-block:statcard-progress BEGIN
    {
      id: "progress",
      label: "Progress",
      value: r.progress_pct,
      format: "text",
      tone: "g-mygiving",
      render: createElement(
        "span",
        { className: "tabular" },
        `${(r.progress_pct ?? 0).toFixed(1)}%`,
      ),
    },
    // override-block:statcard-progress END
    // override-block:statcard-status BEGIN
    {
      id: "status",
      label: "Status",
      value: STATUS_LABEL[r.status] ?? r.status,
      format: "text",
      tone: "g-events",
    },
    // override-block:statcard-status END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.campaign_detail["edit"], "disable") },
    { id: "activate", label: "Activate", variant: "primary", permissionGate: toGate(GATE_CATALOG.campaign_detail["activate"], "disable") },
    { id: "archive", label: "Archive", variant: "ghost", permissionGate: toGate(GATE_CATALOG.campaign_detail["archive"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 5 tabs per campaign-detail-v2.cross-screen.yaml §layout.tabs +
    // CHG-1325 Tags. Overview headlines a ProgressBar (CHG-1236) and
    // surfaces ACI suggestions (CHG-1274). Donations/Donors/Designations/
    // Communications are placeholder bodies citing fetch endpoints.
    {
      id: "overview",
      label: "Overview",
      render: (c, fmt) => {
        const raisedFormatted = fmt.formatCurrency(c.raised_minor_units, c.currency);
        const goalFormatted = fmt.formatCurrency(c.goal_amount_minor_units, c.currency);
        return createElement(
          "div",
          { style: { display: "flex", flexDirection: "column", gap: 16 } },
          createElement(
            "div",
            { className: "card", style: { padding: 16 } },
            createElement(ProgressBar, {
              value: c.raised_minor_units,
              max: c.goal_amount_minor_units,
              tone: "g-giving",
              label: "Raised vs goal",
              valueFormatted: `${raisedFormatted} of ${goalFormatted}`,
              height: 12,
            }),
          ),
          createElement(
            "div",
            { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
            createElement(
              "div",
              null,
              "Start: ",
              c.start_date ? fmt.formatDate(c.start_date, "long") : "—",
            ),
            createElement(
              "div",
              null,
              "End: ",
              c.end_date ? fmt.formatDate(c.end_date, "long") : "—",
            ),
            createElement(
              "div",
              null,
              "Stage last changed: ",
              c.stage_changed_at ? fmt.formatDate(c.stage_changed_at, "relative") : "—",
            ),
            c.description
              ? createElement(
                  "div",
                  { style: { marginTop: 12, color: "var(--color-text-muted)" } },
                  c.description,
                )
              : null,
          ),
          // CHG-1274 — ACI runtime surface 2 (campaign-suggested-actions).
          createElement(ACISurface, {
            surface_id: "campaign-suggested-actions",
            entity_id: c.id,
          }),
        );
      },
    },
    {
      id: "donations",
      label: "Donations",
      render: (c) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Donations — fetched via GET /campaigns/${c.id}/donations. Most-recent-first. Full UI in ε CHG.`,
        ),
    },
    {
      id: "donors",
      label: "Donors",
      render: (c) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Donors — fetched via GET /campaigns/${c.id}/donors. Grouped by donor with total_given_minor_units + donation_count. Full UI in ε CHG.`,
        ),
    },
    {
      id: "designations",
      label: "Designations",
      render: (c) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Designations linked to this campaign — fetched via GET /designations?campaign_id=${c.id} (canonical endpoint pending; see assets.ts note). Full UI in ε CHG.`,
        ),
    },
    {
      id: "communications",
      label: "Communications",
      render: (c) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Delivery logs + subscriber communications — fetched via GET /campaigns/${c.id}/delivery-logs (placeholder). Full UI in ε CHG.`,
        ),
    },
    // CHG-1325 — Tag taxonomy phase 3. Tags applied to this campaign.
    {
      id: "tags",
      label: "Tags",
      render: (c) =>
        createElement(EntityTagsSection, {
          entityType: "campaign",
          entityId: c.id,
        }),
    },
    // override-block:custom-tabs END
  ],
};
